import { call, cancelled, put, takeEvery } from 'redux-saga/effects';

import { callApi } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import {
    getPageSet,
    invalidateUserCollectionsMetadata,
    removePageSetFromCollections,
    updatePageSet,
    upsertPageSetCollections,
} from '@pressreader/social-api';
import { IssuePageSet } from '@pressreader/social-types';

import {
    loadIssuePageSetByIdAction,
    loadIssuePageSetByIdBegan,
    loadIssuePageSetByIdDone,
    loadIssuePageSetByIdFailed,
    loadIssuePageSetByIssueKeyCancelled,
    removePageSetFromCollectionsAction,
    removePageSetFromCollectionsBegan,
    removePageSetFromCollectionsDone,
    removePageSetFromCollectionsFailed,
    updateIssuePageSetByIdAction,
    updateIssuePageSetByIdBegan,
    updateIssuePageSetByIdDone,
    updateIssuePageSetByIdFailed,
    upsertIssuePageSetCollectionAction,
    upsertIssuePageSetCollectionBegan,
    upsertIssuePageSetCollectionDone,
    upsertIssuePageSetCollectionFailed,
} from '../../actions';

import { getOrWaitForPageSet, waitUntilPageSetLoaded } from './issue.pageset.utils';

function* loadPageSetById({ pageSetId, isForce = false }: { isForce?: boolean; pageSetId: string }) {
    const collectionsCached = yield* getOrWaitForPageSet(pageSetId);

    yield put(
        loadIssuePageSetByIdBegan({
            pageSetId,
        }),
    );

    const isPageSetExist = Boolean(collectionsCached) && !isForce;
    const pageSet: IssuePageSet | undefined = isPageSetExist ? collectionsCached : yield call(getPageSet, pageSetId);

    yield put(
        loadIssuePageSetByIdDone({
            pageSet: pageSet ? [pageSet] : [],
            issueKey: pageSet?.issueKey ?? '',
        }),
    );
}

function* handleLoadIssuePageSetByIdAction({ payload }: ReturnType<typeof loadIssuePageSetByIdAction>) {
    try {
        yield* waitUntilPageSetLoaded(payload.pageSetId);

        yield* loadPageSetById({
            pageSetId: payload.pageSetId,
            isForce: payload.isForce,
        });
    } catch (e) {
        error(e);
        yield put(loadIssuePageSetByIdFailed({ pageSetId: payload.pageSetId, error: e }));
    } finally {
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            yield put(loadIssuePageSetByIssueKeyCancelled({ issueKey: payload.pageSetId }));
        }
    }
}

function* handleUpsertIssuePageSetCollection({ payload }: ReturnType<typeof upsertIssuePageSetCollectionAction>) {
    try {
        yield* waitUntilPageSetLoaded(payload.pageSetId);

        yield put(upsertIssuePageSetCollectionBegan(payload));

        yield call(upsertPageSetCollections, payload.pageSetId, payload.collections);
        // contentCount changed - drop the cached collections metadata and let subscribers refresh. AB#173760
        yield call(invalidateUserCollectionsMetadata);

        yield put(upsertIssuePageSetCollectionDone(payload));
    } catch (e) {
        error(e);
        yield put(upsertIssuePageSetCollectionFailed(payload));
    }
}

function* handleRemovePageSetFromCollectionsAction({ payload }: ReturnType<typeof removePageSetFromCollectionsAction>) {
    try {
        yield* waitUntilPageSetLoaded(payload.pageSetId);

        yield put(removePageSetFromCollectionsBegan(payload));

        yield callApi(removePageSetFromCollections, payload.pageSetId);
        yield call(invalidateUserCollectionsMetadata);

        yield put(removePageSetFromCollectionsDone(payload));
    } catch (e) {
        error(e);

        yield put(removePageSetFromCollectionsFailed(payload));
    }
}

function* handleUpdateIssuePageSetByIdAction({ payload }: ReturnType<typeof updateIssuePageSetByIdAction>) {
    try {
        yield* waitUntilPageSetLoaded(payload.pageSetId);

        yield put(
            updateIssuePageSetByIdBegan({
                pageSetId: payload.pageSetId,
            }),
        );

        yield callApi(updatePageSet, payload.pageSetId, payload.pages);

        yield put(updateIssuePageSetByIdDone(payload));
    } catch (e) {
        yield put(updateIssuePageSetByIdFailed({ pageSetId: payload.pageSetId }));
    }
}
function* saga() {
    yield takeEvery(loadIssuePageSetByIdAction, handleLoadIssuePageSetByIdAction);
    yield takeEvery(upsertIssuePageSetCollectionAction, handleUpsertIssuePageSetCollection);
    yield takeEvery(updateIssuePageSetByIdAction, handleUpdateIssuePageSetByIdAction);
    yield takeEvery(removePageSetFromCollectionsAction, handleRemovePageSetFromCollectionsAction);
}

export { saga };
